export type AlertProgress = {
  initiatedOn: number;
  followUps: number;
  retryAttempts?: number;
  lastAttemptAt?: number;
  nextAlertAt?: number;
  scheduledEndAt?: number;
};

export function formatAlertStartedTime(initiatedOn: number): string {
  return new Date(initiatedOn).toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Same three facts on the account list and the account-contact row. */
export function formatAlertProgressLines(progress: AlertProgress): string[] {
  const started = formatAlertStartedTime(progress.initiatedOn);
  const lines = [
    progress.scheduledEndAt != null
      ? `Started: ${started} · Active until: ${formatAlertStartedTime(progress.scheduledEndAt)}`
      : `Started: ${started}`,
  ];

  const attempt = progress.followUps + 1;
  const attemptTime = formatAlertStartedTime(progress.lastAttemptAt ?? progress.initiatedOn);
  const total =
    progress.retryAttempts != null && progress.retryAttempts >= 1
      ? 1 + progress.retryAttempts
      : undefined;
  lines.push(
    total != null
      ? `Attempt ${attempt} of ${total} · ${attemptTime}`
      : `Attempt ${attempt} · ${attemptTime}`,
  );

  if (total != null && attempt < total && progress.nextAlertAt != null) {
    lines.push(`Next alert: ${formatAlertStartedTime(progress.nextAlertAt)}`);
  }

  return lines;
}

/** Upcoming send for dashboard “Next alert” column (matches account contact progress math). */
export function formatNextAlertAttemptLabel(
  progress: Pick<AlertProgress, 'followUps' | 'retryAttempts'>,
): string | null {
  const total =
    progress.retryAttempts != null && progress.retryAttempts >= 1
      ? 1 + progress.retryAttempts
      : null;
  if (total == null) {
    return null;
  }
  const nextAttempt = progress.followUps + 2;
  if (nextAttempt > total) {
    return null;
  }
  return `Attempt ${nextAttempt} of ${total}`;
}
